import type { CustomTestCategory, CustomTestCopyData, CustomTestDetail, CustomTestListItem, CustomTestProduct, SmartBuildRecommendation, SmartBuildRequest } from 'pages/custom_tests/types';
import type { QuestionTreeResult } from 'pages/custom_tests/components/question_picker/QuestionPicker';
import { ApiCaller, FullfilledApiResponse } from 'apis/ApiCaller';
import { Success } from 'types';

export type CustomTestCreateRequest = {
    categoryId: number;
    languageId: number;
    minScore: number;
    minutes: number;
    modeId: number;
    multiProductTest: boolean;
    productId: number;
    questionIds: string;

    /** 1 = randomized delivery order, 2 = curriculum order. 0/undefined = server default. */
    questionOrderMode?: number;
    questionsXml: string;
    randomly: number;
    testName: string;
    totalQuestions: number;
    useRandomly: boolean;
};

export type CustomTestUpdateRequest = {
    isActive: boolean;
    languageId?: number;
    minScore: number;
    minutes: number;
    modeId: number;
    multiProductTest: boolean;
    productId: string;
    questionIds: string;
    questionOrderMode?: number;
    questionsXml: string;
    randomly: number;
    testId: number;
    testName: string;
    totalQuestions: number;
    useRandomly: boolean;
};

export type CustomTestSearchParams = {
    categoryId?: number;
    createdBy?: string;
    minScore?: number;
    modeId?: number;
    productId?: number;
    statusId?: number;
    testName?: string;
};

export type CustomTestPagedParams = {
    includeArchived?: boolean;
    onlyMine?: boolean;
    page?: number;
    pageSize?: number;
    searchTerm?: string;
    sortDir?: 'asc' | 'desc';
    sortField?: string;
};

/** Row shape of the server-paged lookup (serialized PracticeTest entity). */
export type CustomTestPagedItem = {
    AssignmentCount: number;
    Category: { Id: number; Name: string } | null;
    CreatedBy: string;
    Date_create: string;
    HasResults: boolean;
    Id: number;
    IsActive: boolean;

    /** Passing score as a percent (0-100). */
    MinScore: number;
    Minutes: number;

    /** 1 = Training, 2 = Testing, 3 = Both (linked mode pair). */
    ModeId: number;
    Name: string;
    NumQuestions: number;
    OwnerId: number;

    /** Comma-separated program names (single name for single-program tests). */
    StringProduct: string;
};

export type CustomTestPagedResult = {
    Items: CustomTestPagedItem[];
    Page: number;
    PageSize: number;
    TotalCount: number;
};

/** One assignment target row with its per-assignment settings. */
export type CustomTestAssignmentDetail = {
    AllowReview: boolean | null;
    DueDate: string;
    IsActive: boolean;
    IsProctored: boolean;
    MaxAttempts: number | null;
    StartDate: string;
    TargetId: number;
    TargetType: 'transaction' | 'group';
};

export type CustomTestAssignmentSettings = {
    allowReview?: boolean;
    dueDate?: string;
    isProctored?: boolean;
    maxAttempts?: number;
    startDate?: string;
};

/** Licenses granting one of the test's programs; empty Licenses = no active license grants it. */
export type CustomTestRequiredLicense = {
    Licenses: { Id: number; Name: string }[];
    ProductId: number;
    ProductName: string;
};

export type CustomTestProductQuestionCount = {
    DefaultPassingScore: number | null;
    EligibleQuestionCount: number;
    ProductId: number;
};

export type AssignedPartnersInfo = {
    AssignedPartnerIds: number[];
    TestName: string;
};

export type AssignedTransactionsAndGroupsInfo = {
    AssignedStudentGroupIds: number[];
    AssignedTransactionIds: number[];
    TestName: string;
};

export class CustomTestApi {

    private static apiCaller = new ApiCaller('CustomTest');

    public static Search(params: CustomTestSearchParams): Promise<CustomTestListItem[]> {

        return this.apiCaller.GET<CustomTestListItem[]>('Search', {
            categoryId: params.categoryId ?? null,
            minScore: params.minScore ?? null,
            modeId: params.modeId ?? null,
            productId: params.productId ?? null,
            statusId: params.statusId ?? null,
            testName: params.testName ?? null
        });

    }

    /**
     * Server-paged lookup for the rebuilt Custom Tests page. Sorting, paging,
     * search and the Only My Tests / Archived filters run in the database.
     */
    public static SearchPaged(params: CustomTestPagedParams): Promise<CustomTestPagedResult> {

        return this.apiCaller.GET<CustomTestPagedResult>('SearchPaged', {
            includeArchived: params.includeArchived ?? false,
            onlyMine: params.onlyMine ?? false,
            page: params.page ?? 1,
            pageSize: params.pageSize ?? 15,
            searchTerm: params.searchTerm ?? '',
            sortDir: params.sortDir ?? 'desc',
            sortField: params.sortField ?? 'DateCreate'
        });

    }

    public static GetCategories(partnerId: number): Promise<CustomTestCategory[]> {

        return this.apiCaller.GET<CustomTestCategory[]>('GetCategories', { partnerId });

    }

    public static GetProductsByCategory(categoryId: number, partnerId: number): Promise<CustomTestProduct[]> {

        return this.apiCaller.GET<CustomTestProduct[]>('GetProductsByCategory', { categoryId, partnerId });

    }

    // deliveryType selects the In-App (1) vs In-Browser (3) question pool for hybrid categories.
    // It is an explicit user choice, independent of the test's Training/Testing mode.
    public static GetQuestions(
        productIds: string,
        categoryId: number,
        languageId = 0,
        deliveryType = 3,
        selectedIds = ''
    ): Promise<QuestionTreeResult> {

        return this.apiCaller.POST<QuestionTreeResult>('GetQuestions', {
            categoryId,
            deliveryType,
            languageId,
            productIds,
            selectedIds
        });

    }

    public static Get(testId: number): Promise<CustomTestDetail> {

        return this.apiCaller.GET<CustomTestDetail>('Get', { testId });

    }

    /**
     * Fetch a prefilled draft payload for copying an existing test. Read-only: this
     * creates no server record. Save (via Create) produces a new independently owned test.
     */
    public static GetForCopy(testId: number): Promise<CustomTestCopyData> {

        return this.apiCaller.GET<CustomTestCopyData>('GetForCopy', { testId });

    }

    /**
     * Request a Smart Build recommendation draft (spec §13.1). Returns the ranked
     * selection plus objective/viability context. Never persists a test.
     */
    public static SmartBuildRecommend(request: SmartBuildRequest): Promise<SmartBuildRecommendation> {

        return this.apiCaller.POST<SmartBuildRecommendation>('SmartBuildRecommend', {
            categoryId: request.categoryId,
            classroomId: request.classroomId,
            deliveryType: request.deliveryType ?? 3,
            languageId: request.languageId ?? 0,
            productIds: request.productIds.join(','),
            purpose: request.purpose,
            scopeType: request.scopeType,
            scopeValueIds: request.scopeValueIds.join(','),
            size: request.size
        });

    }

    public static Create(data: CustomTestCreateRequest): Promise<FullfilledApiResponse> {

        return this.apiCaller.POST<FullfilledApiResponse>('Create', data);

    }

    public static Update(data: CustomTestUpdateRequest): Promise<FullfilledApiResponse> {

        return this.apiCaller.POST<FullfilledApiResponse>('Update', data);

    }

    public static Clone(testId: number): Promise<Success<void>> {

        return this.apiCaller.POST<Success<void>>('Clone', { testId });

    }

    public static ToggleActive(testId: number): Promise<Success<void>> {

        return this.apiCaller.POST<Success<void>>('ToggleActive', { testId });

    }

    public static Delete(testId: number): Promise<Success<void>> {

        return this.apiCaller.POST<Success<void>>('Delete', { testId });

    }

    /**
     * Rename a custom test without touching any other field. Ownership and
     * per-user name uniqueness are enforced server-side.
     */
    public static Rename(testId: number, testName: string): Promise<Success<void>> {

        return this.apiCaller.POST<Success<void>>('Rename', { testId, testName });

    }

    public static ValidateTestName(testName: string, partnerId: number): Promise<boolean> {

        return this.apiCaller.GET<boolean>('ValidateTestName', { partnerId, testName });

    }

    /**
     * Per-program licenses required for the test's content — powers the details/summary
     * "Required Licenses for Assignment" section and the assign wizard's missing-license check.
     */
    public static GetRequiredLicenses(testId: number): Promise<CustomTestRequiredLicense[]> {

        return this.apiCaller.GET<CustomTestRequiredLicense[]>('GetRequiredLicenses', { testId });

    }

    /**
     * Per-program eligible-question counts for a category (same rules as the question
     * tree) plus the program's default passing score when configured.
     */
    public static GetProductQuestionCounts(categoryId: number, languageId = 1, modeId = 2): Promise<CustomTestProductQuestionCount[]> {

        return this.apiCaller.GET<CustomTestProductQuestionCount[]>('GetProductQuestionCounts', { categoryId, languageId, modeId });

    }

    /**
     * Per-target assignment rows including the stored per-assignment settings
     * (dates, max attempts, proctoring, question review).
     */
    public static GetAssignmentDetails(testId: number, partnerId: number): Promise<CustomTestAssignmentDetail[]> {

        return this.apiCaller.GET<CustomTestAssignmentDetail[]>('GetAssignmentDetails', { partnerId, testId });

    }

    public static GetAssignedPartners(testId: number): Promise<AssignedPartnersInfo> {

        return this.apiCaller.GET<AssignedPartnersInfo>('GetAssignedPartners', { testId });

    }

    public static SaveAssignedPartners(testId: number, partnerIds: number[]): Promise<Success<void>> {

        return this.apiCaller.POST<Success<void>>('SaveAssignedPartners', { partnerIds, testId });

    }

    public static GetAssignedTransactionsAndGroups(testId: number, partnerId: number): Promise<AssignedTransactionsAndGroupsInfo> {

        return this.apiCaller.GET<AssignedTransactionsAndGroupsInfo>('GetAssignedTransactionsAndGroups', { partnerId, testId });

    }

    /**
     * Save assignment targets. Optional settings apply to NEWLY added targets;
     * targets that stay assigned keep their previously configured settings.
     */
    public static SaveAssignedTransactionsAndGroups(
        testId: number,
        partnerId: number,
        transactionIds: number[],
        studentGroupIds: number[],
        settings?: CustomTestAssignmentSettings
    ): Promise<Success<void>> {

        return this.apiCaller.POST<Success<void>>('SaveAssignedTransactionsAndGroups', {
            allowReview: settings?.allowReview ?? null,
            dueDate: settings?.dueDate ?? null,
            isProctored: settings?.isProctored ?? null,
            maxAttempts: settings?.maxAttempts ?? null,
            partnerId,
            startDate: settings?.startDate ?? null,
            studentGroupIds,
            testId,
            transactionIds
        });

    }

}
